import { useEffect, useMemo, useState } from 'react';
import { CalendarCheck } from 'lucide-react';
import { fieldUserApi } from '@/features/auth/api';
import { operationsApi, type AttendanceRow, type LeaveRequest, type LeaveType } from '../api';
import { PageHeader } from '@/lib/ui';

type Tab = 'calendar' | 'leave';
type ViewMode = 'daily' | 'monthly';

type FieldUser = {
  id: string;
  employee_id: string;
  full_name: string;
  status: string;
};

const statusClass: Record<AttendanceRow['status'], string> = {
  PRESENT: 'bg-emerald-500/15 text-emerald-300',
  PARTIAL: 'bg-amber-500/15 text-amber-300',
  ABSENT: 'bg-rose-500/15 text-rose-300',
  LEAVE: 'bg-blue-500/15 text-blue-300',
  HOLIDAY: 'bg-violet-500/15 text-violet-300',
  WEEKEND: 'bg-slate-500/15 text-slate-300',
};

const LEAVE_TYPES: LeaveType[] = ['ANNUAL', 'SICK', 'UNPAID', 'EMERGENCY', 'OTHER'];

export function AttendancePage() {
  const [tab, setTab] = useState<Tab>('calendar');
  const [view, setView] = useState<ViewMode>('monthly');
  const [users, setUsers] = useState<FieldUser[]>([]);
  const [userId, setUserId] = useState('');
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState<AttendanceRow[]>([]);
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [manualFor, setManualFor] = useState<AttendanceRow | null>(null);
  const [correctFor, setCorrectFor] = useState<AttendanceRow | null>(null);
  const [decideFor, setDecideFor] = useState<{
    leave: LeaveRequest;
    decision: 'APPROVE' | 'REJECT';
  } | null>(null);
  const [showNewLeave, setShowNewLeave] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const list = (await fieldUserApi.list()) as FieldUser[];
        setUsers(list);
        if (list.length && !userId) setUserId(list[0].id);
      } catch (e) {
        setError((e as Error).message || 'Failed to load field users');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { start, end } = useMemo(() => {
    if (view === 'daily') {
      return { start: day, end: day };
    }
    const [y, m] = month.split('-').map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    return {
      start: first.toISOString().slice(0, 10),
      end: last.toISOString().slice(0, 10),
    };
  }, [view, day, month]);

  const loadAttendance = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await operationsApi.listAttendance(userId, {
        start: new Date(start).toISOString(),
        end: new Date(`${end}T23:59:59`).toISOString(),
      });
      setRows(data);
    } catch (e) {
      setError((e as Error).message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const loadLeave = async () => {
    setLoading(true);
    setError(null);
    try {
      setLeaves(await operationsApi.listPendingLeave());
    } catch (e) {
      setError((e as Error).message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'leave') void loadLeave();
  }, [tab]);

  // Auto-reload the calendar when the user/window changes.
  useEffect(() => {
    if (tab === 'calendar' && userId) void loadAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, start, end, tab]);

  const exportCsv = () => {
    const header = [
      'Date',
      'Status',
      'Worked',
      'Regular',
      'Overtime',
      'First in',
      'Last out',
      'Manual',
      'Note',
    ];
    const lines = rows.map((r) =>
      [
        new Date(r.work_date).toISOString().slice(0, 10),
        r.status,
        r.worked_hours,
        r.regular_hours,
        r.overtime_hours,
        r.first_in_at ?? '',
        r.last_out_at ?? '',
        r.is_manual ? 'yes' : 'no',
        (r.correction_note ?? '').replace(/"/g, '""'),
      ]
        .map((v) => `"${String(v)}"`)
        .join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const who = users.find((u) => u.id === userId)?.employee_id ?? 'user';
    a.download = `attendance-${who}-${start}_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-5">
        <PageHeader
          title="Attendance & Leave"
          icon={CalendarCheck}
          subtitle="Calendar derived from duty sessions + manual entries; leave approval queue."
          className="!mb-0"
        />
      </div>
      <div className="flex gap-1 border-b border-border px-4">
        <TabBtn active={tab === 'calendar'} onClick={() => setTab('calendar')}>
          Calendar
        </TabBtn>
        <TabBtn active={tab === 'leave'} onClick={() => setTab('leave')}>
          Leave queue
          {leaves.length > 0 && (
            <span className="ml-2 rounded bg-rose-500/20 px-1 text-xs text-rose-300">
              {leaves.length}
            </span>
          )}
        </TabBtn>
      </div>

      {error && (
        <div className="border-b border-rose-500/40 bg-rose-500/10 px-6 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-auto p-6">
        {tab === 'calendar' && (
          <>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-xs uppercase text-muted-foreground">Field user</span>
                <select
                  className="input w-[20rem]"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                >
                  <option value="">— select —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.employee_id} · {u.full_name}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex flex-col gap-1 text-sm">
                <span className="text-xs uppercase text-muted-foreground">View</span>
                <div className="flex overflow-hidden rounded border border-border">
                  <ViewToggle active={view === 'daily'} onClick={() => setView('daily')}>
                    Daily
                  </ViewToggle>
                  <ViewToggle active={view === 'monthly'} onClick={() => setView('monthly')}>
                    Monthly
                  </ViewToggle>
                </div>
              </div>

              {view === 'daily' ? (
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-xs uppercase text-muted-foreground">Date</span>
                  <input
                    type="date"
                    className="input"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                  />
                </label>
              ) : (
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-xs uppercase text-muted-foreground">Month</span>
                  <input
                    type="month"
                    className="input"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  />
                </label>
              )}

              <button className="btn-secondary" disabled={!userId} onClick={loadAttendance}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
              <button className="btn-secondary" disabled={rows.length === 0} onClick={exportCsv}>
                Export CSV
              </button>
              <div className="ml-auto">
                <button className="btn-primary" onClick={() => setShowNewLeave(true)}>
                  + New leave
                </button>
              </div>
            </div>

            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Worked</th>
                  <th className="py-2 pr-4">Regular</th>
                  <th className="py-2 pr-4">Overtime</th>
                  <th className="py-2 pr-4">In</th>
                  <th className="py-2 pr-4">Out</th>
                  <th className="py-2 pr-4">Source</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60">
                    <td className="py-2 pr-4 font-mono text-xs">
                      {new Date(r.work_date).toLocaleDateString()}
                    </td>
                    <td className="py-2 pr-4">
                      {r.status === 'ABSENT' ? (
                        <button
                          title="Add manual entry"
                          onClick={() => setManualFor(r)}
                          className={
                            'rounded px-2 py-0.5 text-xs hover:ring-1 hover:ring-primary ' +
                            statusClass[r.status]
                          }
                        >
                          {r.status} +
                        </button>
                      ) : (
                        <span className={'rounded px-2 py-0.5 text-xs ' + statusClass[r.status]}>
                          {r.status}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">{r.worked_hours.toFixed(2)} h</td>
                    <td className="py-2 pr-4 tabular-nums">{r.regular_hours.toFixed(2)} h</td>
                    <td className="py-2 pr-4 tabular-nums">
                      {r.overtime_hours.toFixed(2)} h
                      {r.overtime_multiplier_used && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ×{r.overtime_multiplier_used}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      {r.first_in_at ? new Date(r.first_in_at).toLocaleTimeString() : '—'}
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      {r.last_out_at ? new Date(r.last_out_at).toLocaleTimeString() : '—'}
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      {r.is_manual ? (
                        <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-300">
                          manual
                        </span>
                      ) : r.corrected_by_user_id ? (
                        <span className="text-muted-foreground">corrected</span>
                      ) : (
                        <span className="text-muted-foreground">derived</span>
                      )}
                    </td>
                    <td className="py-2 text-right text-xs">
                      <button
                        className="text-primary hover:underline"
                        onClick={() => setCorrectFor(r)}
                      >
                        Correct
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && userId && (
                  <tr>
                    <td colSpan={9} className="py-6 text-center text-sm text-muted-foreground">
                      No attendance rows for the selected window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {tab === 'leave' && (
          <>
            <p className="mb-3 text-sm text-muted-foreground">
              {leaves.length} pending request{leaves.length === 1 ? '' : 's'}
            </p>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-4">Field user</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">From</th>
                  <th className="py-2 pr-4">To</th>
                  <th className="py-2 pr-4">Reason</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {leaves.map((l) => (
                  <tr key={l.id} className="border-b border-border/60">
                    <td className="py-2 pr-4 text-xs">{userLabel(users, l.field_user_id)}</td>
                    <td className="py-2 pr-4">{l.leave_type}</td>
                    <td className="py-2 pr-4">{new Date(l.starts_on).toLocaleDateString()}</td>
                    <td className="py-2 pr-4">{new Date(l.ends_on).toLocaleDateString()}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{l.reason ?? '—'}</td>
                    <td className="space-x-3 py-2 text-right text-xs">
                      <button
                        className="text-emerald-300 hover:underline"
                        onClick={() => setDecideFor({ leave: l, decision: 'APPROVE' })}
                      >
                        Approve
                      </button>
                      <button
                        className="text-rose-300 hover:underline"
                        onClick={() => setDecideFor({ leave: l, decision: 'REJECT' })}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && leaves.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      No pending leave requests.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      {manualFor && (
        <ManualEntryModal
          row={manualFor}
          onClose={() => setManualFor(null)}
          onSaved={async () => {
            setManualFor(null);
            await loadAttendance();
          }}
          submit={(body) => operationsApi.correctAttendance(userId, body)}
        />
      )}
      {correctFor && (
        <CorrectionModal
          row={correctFor}
          onClose={() => setCorrectFor(null)}
          onSaved={async () => {
            setCorrectFor(null);
            await loadAttendance();
          }}
          submit={(body) => operationsApi.correctAttendance(userId, body)}
        />
      )}
      {decideFor && (
        <LeaveDecisionModal
          leave={decideFor.leave}
          decision={decideFor.decision}
          onClose={() => setDecideFor(null)}
          onDone={async () => {
            setDecideFor(null);
            await loadLeave();
          }}
        />
      )}
      {showNewLeave && (
        <NewLeaveModal
          users={users}
          defaultUserId={userId}
          onClose={() => setShowNewLeave(false)}
          onCreated={async () => {
            setShowNewLeave(false);
            await loadLeave();
          }}
        />
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────
function userLabel(users: FieldUser[], id: string): string {
  const u = users.find((x) => x.id === id);
  return u ? `${u.employee_id} · ${u.full_name}` : id;
}

function dateOf(workDate: string): string {
  return new Date(workDate).toISOString().slice(0, 10);
}

/** Combine a YYYY-MM-DD date with an HH:MM time into an ISO datetime. */
function toIso(date: string, time: string): string {
  return new Date(`${date}T${time}:00`).toISOString();
}

// ── Manual entry modal (WEB-TC-68) ───────────────────────────────────────
function ManualEntryModal({
  row,
  onClose,
  onSaved,
  submit,
}: {
  row: AttendanceRow;
  onClose: () => void;
  onSaved: () => void;
  submit: (body: Parameters<typeof operationsApi.correctAttendance>[1]) => Promise<unknown>;
}) {
  const date = dateOf(row.work_date);
  const [clockIn, setClockIn] = useState('06:30');
  const [clockOut, setClockOut] = useState('17:00');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!note.trim()) {
      setErr('A note is required.');
      return;
    }
    if (clockOut <= clockIn) {
      setErr('Clock-out must be after clock-in.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await submit({
        work_date: new Date(`${date}T00:00:00`).toISOString(),
        status: 'PRESENT',
        worked_hours: 0, // backend derives from the clock span
        correction_note: note.trim(),
        first_in_at: toIso(date, clockIn),
        last_out_at: toIso(date, clockOut),
        is_manual: true,
      });
      onSaved();
    } catch (e) {
      setErr((e as Error).message || 'Failed to save');
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`Manual entry — ${date}`} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Labeled label="Clock-in">
          <input
            type="time"
            className="input"
            value={clockIn}
            onChange={(e) => setClockIn(e.target.value)}
          />
        </Labeled>
        <Labeled label="Clock-out">
          <input
            type="time"
            className="input"
            value={clockOut}
            onChange={(e) => setClockOut(e.target.value)}
          />
        </Labeled>
      </div>
      <Labeled label="Note">
        <input
          className="input w-full"
          placeholder="e.g. Forgot phone"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          autoFocus
        />
      </Labeled>
      {err && <p className="text-xs text-rose-300">{err}</p>}
      <ModalActions onClose={onClose} onSave={save} busy={busy} saveLabel="Add entry" />
    </ModalShell>
  );
}

// ── Correction modal ──────────────────────────────────────────────────────
function CorrectionModal({
  row,
  onClose,
  onSaved,
  submit,
}: {
  row: AttendanceRow;
  onClose: () => void;
  onSaved: () => void;
  submit: (body: Parameters<typeof operationsApi.correctAttendance>[1]) => Promise<unknown>;
}) {
  const date = dateOf(row.work_date);
  const [status, setStatus] = useState<AttendanceRow['status']>(row.status);
  const [worked, setWorked] = useState(String(row.worked_hours));
  const [overtime, setOvertime] = useState(String(row.overtime_hours));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!note.trim()) {
      setErr('A correction note is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await submit({
        work_date: new Date(`${date}T00:00:00`).toISOString(),
        status,
        worked_hours: Number(worked) || 0,
        overtime_hours: Number(overtime) || 0,
        correction_note: note.trim(),
      });
      onSaved();
    } catch (e) {
      setErr((e as Error).message || 'Failed to save');
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`Correct attendance — ${date}`} onClose={onClose}>
      <Labeled label="Status">
        <select
          className="input w-full"
          value={status}
          onChange={(e) => setStatus(e.target.value as AttendanceRow['status'])}
        >
          {(['PRESENT', 'PARTIAL', 'ABSENT', 'LEAVE', 'HOLIDAY', 'WEEKEND'] as const).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Labeled>
      <div className="grid grid-cols-2 gap-3">
        <Labeled label="Worked hours">
          <input
            type="number"
            step="0.25"
            className="input"
            value={worked}
            onChange={(e) => setWorked(e.target.value)}
          />
        </Labeled>
        <Labeled label="Overtime hours">
          <input
            type="number"
            step="0.25"
            className="input"
            value={overtime}
            onChange={(e) => setOvertime(e.target.value)}
          />
        </Labeled>
      </div>
      <Labeled label="Correction note">
        <input
          className="input w-full"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          autoFocus
        />
      </Labeled>
      {err && <p className="text-xs text-rose-300">{err}</p>}
      <ModalActions onClose={onClose} onSave={save} busy={busy} saveLabel="Save" />
    </ModalShell>
  );
}

// ── Leave decision modal (WEB-TC-72) ──────────────────────────────────────
function LeaveDecisionModal({
  leave,
  decision,
  onClose,
  onDone,
}: {
  leave: LeaveRequest;
  decision: 'APPROVE' | 'REJECT';
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (decision === 'REJECT' && !note.trim()) {
      setErr('A note is required when rejecting.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await operationsApi.decideLeave(leave.id, {
        decision,
        note: note.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setErr((e as Error).message || 'Failed');
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`${decision === 'APPROVE' ? 'Approve' : 'Reject'} leave`} onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        {leave.leave_type} · {new Date(leave.starts_on).toLocaleDateString()} →{' '}
        {new Date(leave.ends_on).toLocaleDateString()}
      </p>
      <Labeled label={decision === 'REJECT' ? 'Note (required)' : 'Note (optional)'}>
        <input
          className="input w-full"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          autoFocus
        />
      </Labeled>
      {err && <p className="text-xs text-rose-300">{err}</p>}
      <ModalActions
        onClose={onClose}
        onSave={save}
        busy={busy}
        saveLabel={decision === 'APPROVE' ? 'Approve' : 'Reject'}
        danger={decision === 'REJECT'}
      />
    </ModalShell>
  );
}

// ── New leave modal (WEB-TC-71) ───────────────────────────────────────────
function NewLeaveModal({
  users,
  defaultUserId,
  onClose,
  onCreated,
}: {
  users: FieldUser[];
  defaultUserId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [fieldUserId, setFieldUserId] = useState(defaultUserId);
  const [leaveType, setLeaveType] = useState<LeaveType>('ANNUAL');
  const today = new Date().toISOString().slice(0, 10);
  const [startsOn, setStartsOn] = useState(today);
  const [endsOn, setEndsOn] = useState(today);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!fieldUserId) {
      setErr('Pick a field user.');
      return;
    }
    if (endsOn < startsOn) {
      setErr('End date must be on or after the start date.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await operationsApi.createLeave({
        field_user_id: fieldUserId,
        leave_type: leaveType,
        starts_on: new Date(`${startsOn}T00:00:00`).toISOString(),
        ends_on: new Date(`${endsOn}T00:00:00`).toISOString(),
        reason: reason.trim() || undefined,
      });
      onCreated();
    } catch (e) {
      setErr((e as Error).message || 'Failed to create');
      setBusy(false);
    }
  };

  return (
    <ModalShell title="New leave request" onClose={onClose}>
      <Labeled label="Field user">
        <select
          className="input w-full"
          value={fieldUserId}
          onChange={(e) => setFieldUserId(e.target.value)}
        >
          <option value="">— select —</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.employee_id} · {u.full_name}
            </option>
          ))}
        </select>
      </Labeled>
      <Labeled label="Type">
        <select
          className="input w-full"
          value={leaveType}
          onChange={(e) => setLeaveType(e.target.value as LeaveType)}
        >
          {LEAVE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </Labeled>
      <div className="grid grid-cols-2 gap-3">
        <Labeled label="From">
          <input
            type="date"
            className="input"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
        </Labeled>
        <Labeled label="To">
          <input
            type="date"
            className="input"
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
          />
        </Labeled>
      </div>
      <Labeled label="Reason (optional)">
        <input
          className="input w-full"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Labeled>
      {err && <p className="text-xs text-rose-300">{err}</p>}
      <ModalActions onClose={onClose} onSave={save} busy={busy} saveLabel="Submit" />
    </ModalShell>
  );
}

// ── Shared UI primitives ──────────────────────────────────────────────────
function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 px-4">
      <div className="card w-full max-w-md space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ModalActions({
  onClose,
  onSave,
  busy,
  saveLabel,
  danger,
}: {
  onClose: () => void;
  onSave: () => void;
  busy: boolean;
  saveLabel: string;
  danger?: boolean;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button
        type="button"
        className={danger ? 'btn-danger' : 'btn-primary'}
        onClick={onSave}
        disabled={busy}
      >
        {busy ? 'Saving…' : saveLabel}
      </button>
    </div>
  );
}

function TabBtn({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'border-b-2 px-3 py-2 text-sm transition-colors ' +
        (active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground')
      }
    >
      {children}
    </button>
  );
}

function ViewToggle({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'px-3 py-1.5 text-sm transition-colors ' +
        (active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground')
      }
    >
      {children}
    </button>
  );
}
